// ABOUTME: W4 admin analysis API — hypothesis readiness + queueing deep-analysis runs

import { apiClient } from './client'

export interface HypothesisReadiness {
  hypothesis_id: string
  statement: string
  theme: string | null
  evidence_count: number
  interview_count: number
  analysis_count: number
  last_analysis_at: string | null
  evidence_points_analyzed: number | null
  last_verdict: string | null
  last_confidence: string | null
  status: 'never' | 'fresh' | 'stale' | 'no-evidence'
}

export const analysisApi = {
  async getReadiness(): Promise<HypothesisReadiness[]> {
    const response = await apiClient.get<HypothesisReadiness[]>('/api/admin/hypotheses/readiness')
    return response.data
  },

  async analyze(hypothesisIds: string[]): Promise<{ job_ids: string[]; skipped: string[] }> {
    const response = await apiClient.post<{ job_ids: string[]; skipped: string[] }>(
      '/api/admin/hypotheses/analyze',
      { hypothesis_ids: hypothesisIds },
    )
    return response.data
  },
}

// --- Hypothesis editing (#29) ---

export interface SignalItem {
  element_id: string
  kind: 'positive' | 'negative' | 'ambiguity' | 'expected_absence'
  text: string
}

export interface HypothesisFramework {
  hypothesis_id: string
  statement: string
  theme: string | null
  archived: boolean
  signals: SignalItem[]
}

export const hypothesisEditApi = {
  async getFramework(hypothesisId: string): Promise<HypothesisFramework> {
    const response = await apiClient.get<HypothesisFramework>(
      `/api/admin/hypotheses/${hypothesisId}/framework`,
    )
    return response.data
  },

  async patchHypothesis(hypothesisId: string, patch: { statement?: string; archived?: boolean }) {
    const response = await apiClient.patch(`/api/admin/hypotheses/${hypothesisId}`, patch)
    return response.data
  },

  async createHypothesis(statement: string): Promise<{ hypothesis_id: string }> {
    const response = await apiClient.post('/api/admin/hypotheses', { statement })
    return response.data
  },

  async addSignal(hypothesisId: string, kind: SignalItem['kind'], text: string): Promise<SignalItem> {
    const response = await apiClient.post(`/api/admin/hypotheses/${hypothesisId}/signals`, { kind, text })
    return response.data
  },

  async editSignal(elementId: string, text: string) {
    const response = await apiClient.patch(
      `/api/admin/hypotheses/signals/${encodeURIComponent(elementId)}`, { text })
    return response.data
  },

  async deleteSignal(elementId: string) {
    const response = await apiClient.delete(
      `/api/admin/hypotheses/signals/${encodeURIComponent(elementId)}`)
    return response.data
  },
}
